// ---------------------------------------------------------------------------
// ChannelCardMetrics — 5-cell metrics row driven by the B1 period block.
//
// Layout (when period present):
//   FOLLOWERS·all   POSTS·{w}   COMMENTS·{w}   ENG. RATE·{w}   PROFILES·{w}
//
// Layout (when period absent — no date range yet):
//   FOLLOWERS·all   POSTS·all   COMMENTS·all   (eng. rate HIDDEN)   PROFILES·all
//
// Posts and Comments totals are computed by summing sparkline data so
// the numbers are always consistent with what the chart shows.
// ---------------------------------------------------------------------------

import React from 'react';
import { Link } from 'react-router-dom';
import { formatNumber } from '@/utils/formatters';
import type { TimePeriod } from '@/components/v11/discovery/types';
import type { Channel } from '@/services/v11/channelsService';
import { DeltaIndicator } from './DeltaIndicator';
import type { ChannelCardMetricsProps } from './types';

const WINDOW_LABEL: Readonly<Record<TimePeriod, string>> = {
  today: 'today',
  yesterday: 'yest',
  '7d': '7d',
  '15d': '15d',
  '30d': '30d',
  '3m': '3m',
  '6m': '6m',
  '12m': '12m',
  wtd: 'wtd',
  mtd: 'mtd',
  qtd: 'qtd',
  ytd: 'ytd',
  all: 'all',
  custom: 'period',
};

const NO_DATA_TOOLTIP =
  'No data in this window. Channel may have incomplete scraping coverage for this period.';

function buildDiscoveryUrl(channel: Channel, period: TimePeriod, source?: string): string {
  const periodParam = period && period !== '30d' ? `&period=${period}` : '';
  const sourceParam = source && source !== 'profiles' ? `&source=${source}` : '';
  return `/v11/discovery?f_channel=${channel.authorId}&f_channel_labels=${encodeURIComponent(channel.username)}&f_channel_platforms=${encodeURIComponent(channel.platform)}${periodParam}${sourceParam}`;
}

export function ChannelCardMetrics({ channel, period, sparklineData, isPeriodLoading }: ChannelCardMetricsProps) {
  const windowLabel = WINDOW_LABEL[period];
  const hasActivity = channel.totalPosts > 100 || channel.commentsReceived > 1000;
  const showPeriod = channel.period !== null;
  const showPeriodPlaceholder = isPeriodLoading && !showPeriod;

  const use5Cols = showPeriod || showPeriodPlaceholder;

  return (
    <div
      className={`px-4 py-3 border-t border-v11-border grid gap-1 ${
        use5Cols ? 'grid-cols-5' : 'grid-cols-4'
      }`}
    >
      <MetricCell label={`FOLLOWERS·all`} value={formatNumber(channel.followers)} />

      {showPeriod ? (
        <MetricCell
          label={`POSTS·${windowLabel}`}
          value={formatNumber(channel.period!.postsInPeriod)}
          showNoData={channel.period!.postsInPeriod === 0 && hasActivity}
          href={buildDiscoveryUrl(channel, period, 'content')}
        />
      ) : showPeriodPlaceholder ? (
        <MetricCell label={`POSTS·${windowLabel}`} value="--" />
      ) : (
        <MetricCell label="POSTS·all" value={formatNumber(channel.totalPosts)} href={buildDiscoveryUrl(channel, period, 'content')} />
      )}

      {showPeriod ? (
        <MetricCell
          label={`COMMENTS·${windowLabel}`}
          value={formatNumber(channel.period!.commentsInPeriod)}
          showNoData={channel.period!.commentsInPeriod === 0 && hasActivity}
          href={buildDiscoveryUrl(channel, period, 'comments')}
        />
      ) : showPeriodPlaceholder ? (
        <MetricCell label={`COMMENTS·${windowLabel}`} value="--" />
      ) : (
        <MetricCell label="COMMENTS·all" value={formatNumber(channel.commentsReceived)} href={buildDiscoveryUrl(channel, period, 'comments')} />
      )}

      {showPeriod ? (
        <MetricCell
          label={`ENG. RATE·${windowLabel}`}
          value={`${channel.period!.engagementRateInPeriod.toFixed(1)}%`}
          delta={<DeltaIndicator value={channel.period!.engagementDelta} unit="pp" />}
        />
      ) : showPeriodPlaceholder ? (
        <MetricCell label={`ENG. RATE·${windowLabel}`} value="--" />
      ) : null}

      {showPeriod ? (
        <MetricCell
          label={`PROFILES·${windowLabel}`}
          value={formatNumber(channel.period!.profilesAddedInPeriod)}
          showNoData={channel.period!.profilesAddedInPeriod === 0 && hasActivity}
          delta={<DeltaIndicator value={channel.period!.profilesDelta} />}
          href={buildDiscoveryUrl(channel, period, 'profiles')}
        />
      ) : showPeriodPlaceholder ? (
        <MetricCell label={`PROFILES·${windowLabel}`} value="--" />
      ) : (
        <MetricCell label="PROFILES·all" value={formatNumber(channel.profilesAdded)} href={buildDiscoveryUrl(channel, period, 'profiles')} />
      )}
    </div>
  );
}

function MetricCell({
  label,
  value,
  delta,
  showNoData,
  href,
}: {
  readonly label: string;
  readonly value: string;
  readonly delta?: React.ReactNode;
  readonly showNoData?: boolean;
  readonly href?: string;
}) {
  const content = (
    <>
      <p className="text-[10px] text-v11-text-muted uppercase tracking-wider">{label}</p>
      <div className="flex items-baseline justify-center gap-1.5 mt-0.5">
        <p className="text-sm font-semibold text-v11-text-primary tabular-nums">{value}</p>
        {showNoData && (
          <span
            aria-label={NO_DATA_TOOLTIP}
            title={NO_DATA_TOOLTIP}
            className="text-[10px] text-v11-text-muted cursor-help"
          >
            ℹ️
          </span>
        )}
        {delta && <span className="text-[10px]">{delta}</span>}
      </div>
    </>
  );

  if (href) {
    return (
      <Link to={href} className="text-center hover:opacity-75 transition-opacity" data-metric={label} title="View in Discovery">
        {content}
      </Link>
    );
  }

  return (
    <div className="text-center" data-metric={label}>
      {content}
    </div>
  );
}

export type { Channel };
